import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = process.env.REACT_APP_BACKEND_URL || "https://nan.nandev.cloud";

export default function ProjectPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects/${slug}`);
        setProject(data && data.id ? data : null);
      } catch {
        setProject(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useSEO({
    title: project ? `${project.title} — ${project.client}` : "Project",
    description: project
      ? project.description
      : "NaN Studio project showcase.",
    url: `${SITE}/portfolio/${slug}`,
  });

  if (loading) {
    return (
      <div className="bg-[#050505] text-white min-h-screen flex items-center justify-center">
        <Nav />
        <p className="text-white/40 text-sm">Loading…</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-[#050505] text-white min-h-screen">
        <Nav />
        <main className="pt-40 pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white mb-12"
          >
            <ArrowLeft size={14} /> Back to portfolio
          </Link>
          <p className="text-white/40">Project not found.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#050505] text-white min-h-screen">
      <Nav />

      <main className="pt-32 md:pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white mb-16"
          >
            <ArrowLeft size={14} /> All projects
          </Link>

          <div className="grid md:grid-cols-12 gap-12 md:gap-20">
            <motion.div
              className="md:col-span-7"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              {project.image_url && (
                <div className="aspect-[16/9] overflow-hidden mb-10">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    width="1200"
                    height="675"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </motion.div>

            <motion.div
              className="md:col-span-5"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-[#29ABE2] mb-3">
                {project.client}
              </div>
              <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tighter text-4xl md:text-5xl lg:text-6xl mb-4">
                {project.title}
              </h1>
              <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-white/40 mb-8">
                {project.discipline} · {project.year}
              </div>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10">
                {project.description}
              </p>

              {project.tags && project.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-10">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full border border-white/[0.12] font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/60"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {project.case_url && (
                <a
                  href={project.case_url}
                  target={project.case_url.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#29ABE2] text-black font-medium text-sm hover:bg-[#5cc2ec] transition-colors"
                >
                  View live project <ArrowUpRight size={14} />
                </a>
              )}
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
